import axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { RecurrenceFrequency } from '../models/recurringRevenue';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 448 — a SKU line on a new recurring invoice (clientKey ties earners to it
// before the server assigns real ids)
export interface RecurringLineInput {
  clientKey: string;
  sku: string;
  brand: string;
  description: string;
  monthlyAmount: number;
  itemId?: string | null;
}

// item 448 — a manual recurring commission: who / which line / %
export interface RecurringEarnerInput {
  userId: string;
  lineClientKey?: string | null;
  percent: number;
  // item 765 — which reason the earner rides (IO Sales / Sales PO), from the pre-fill mapping
  commissionReasonId?: string | null;
}

export interface CreateRecurringInvoiceInput {
  name: string;
  projectId: string;
  customerId: string;
  customerEmail?: string; // item 726/738 — who Stripe emails the invoice to (editable in the enroll panel)
  ccEmail?: string; // item 738 — optional CC mailed a copy of every monthly invoice (via webhook)
  proposalId?: string; // item 746 — the proposal this sub is enrolled from (ledger + label)
  customerPoNumber?: string; // item 746 — client PO (optional; rides the invoice memo/footer)
  workOrderId?: string; // item 881 — the work order a MANUAL subscription bills under (required)
  endDate?: string | null; // item 883 — end date (Stripe cancel_at); null = forever
  headerNote?: string; // item 878 — optional memo printed at the TOP of every monthly invoice
  footerNote?: string; // item 878 — optional note printed at the BOTTOM of every monthly invoice
  frequency: RecurrenceFrequency;
  customIntervalDays?: number | null;
  serviceCategory?: string;
  startDate?: string | null;
  allowCard?: boolean;
  lines: RecurringLineInput[];
  earners: RecurringEarnerInput[];
  // item 1360 — what the subscription delivers (required on a manual add; verified by the creator)
  products?: {
    isRnwy: boolean; isSwitchPilot: boolean; isHelpDesk: boolean; helpDeskTier: number; isThirdParty: boolean; verified: boolean;
  };
}

// item 448 — create a first-class recurring invoice (the ONLY place recurring
// commissions are entered).
const useRecurringRevenueCreate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (input: CreateRecurringInvoiceInput) => {
      const { data } = await axios.post(`${apiUrl()}RecurringRevenue/Create`, input);
      return data as string;
    },
    {
      onSuccess: () => {
        // dart (2026-07-15, Chad) — NEXT_EPIC #3: also light the sidebar/project "recurring" indicator
        // the moment a subscription is enrolled (refresh the project list + detail).
        queryClient.invalidateQueries('recurringRevenue');
        queryClient.invalidateQueries('projects');
        queryClient.invalidateQueries('project');
      },
    },
  );
};

export default useRecurringRevenueCreate;
